/** Dropdown rows for named masters (mood, theme, genre, language, and the rest). */

type MasterApi = {
  get: (collection: string, params?: Record<string, unknown>) => Promise<any>;
};

/** Masters whose dropdowns bind `name`. Each one is loaded in full, not the first API page. */
export const NAMED_MASTER_COLLECTIONS = [
  'genres',
  'languages',
  'contribution_roles',
  'versions',
  'moods',
  'themes',
  'societies',
  'tags_keywords',
] as const;

const PAGE_SIZE = 500;
const MAX_PAGES = 20;

export function readStoredClassification(key: string): any[] {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? '[]');
    return sortClassification(normalizeClassificationRows(raw));
  } catch {
    return [];
  }
}

/**
 * Directus returns 100 rows unless a limit is set, and the app cache used to
 * stop at the first failed call. Load every page, and keep a good cache if
 * the request fails.
 */
export async function loadClassificationMaster(
  api: MasterApi,
  collection: string,
  storageKey: string,
  fields: string,
): Promise<any[]> {
  const cached = readStoredClassification(storageKey);
  const attempts: Record<string, unknown>[] = [
    { sort: 'name', fields },
    { sort: 'name' },
    {},
  ];

  for (const params of attempts) {
    try {
      const rows = sortClassification(normalizeClassificationRows(await fetchAllMasterPages(api, collection, params)));
      if (rows.length) {
        localStorage.setItem(storageKey, JSON.stringify(rows));
        return rows;
      }
      return cached;
    } catch (error) {
      console.warn(`[classification-masters] ${collection} load failed`, error);
    }
  }
  return cached;
}

/** Load every named master into localStorage and return the dropdown rows. */
export async function loadNamedMasters(
  api: MasterApi,
  collections: readonly string[] = NAMED_MASTER_COLLECTIONS,
  fields = 'id,name',
): Promise<Record<string, any[]>> {
  const pairs = await Promise.all(
    collections.map(async (collection) => {
      const rows = await loadClassificationMaster(api, collection, collection, fields);
      return [collection, rows] as const;
    }),
  );
  return Object.fromEntries(pairs);
}

/** Every row of a master collection. Directus returns one page unless offset is advanced. */
export async function fetchAllMasterPages(
  api: MasterApi,
  collection: string,
  params: Record<string, unknown> = {},
): Promise<unknown[]> {
  const rows: unknown[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await api.get(collection, {
      ...params,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    const batch = Array.isArray(res?.data) ? res.data : [];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) {
      return rows;
    }
  }
  return rows;
}

function normalizeClassificationRows(data: unknown): any[] {
  if (!Array.isArray(data)) {
    return [];
  }
  const rows: any[] = [];
  for (const row of data) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const record = row as Record<string, unknown>;
    const name = String(record['name'] ?? record['title'] ?? record['label'] ?? record['email'] ?? '').trim();
    if (!name) {
      continue;
    }
    rows.push({ ...record, name });
  }
  return rows;
}

function sortClassification(items: any[]): any[] {
  return [...items].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'en', { sensitivity: 'base' }));
}
